import React, { useEffect, useState } from 'react';
import './Gallery.css';

const Gallery: React.FC = () => {
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  useEffect(() => {
    fetch('/gallery_image_urls.json')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load gallery images');
        return res.json();
      })
      .then((data) => {
        // Sort photos by the number prefix in their filename
        const sortedPhotos = data.sort((a: string, b: string) => {
          const aMatch = a.match(/gallery\/(\d+)_(.+)$/);
          const bMatch = b.match(/gallery\/(\d+)_(.+)$/);
          
          if (aMatch && bMatch) {
            const aNum = parseInt(aMatch[1], 10);
            const bNum = parseInt(bMatch[1], 10);
            return aNum - bNum;
          }
          
          // Fallback to alphabetical sorting if no number prefix
          return a.localeCompare(b);
        });
        
        setPhotos(sortedPhotos);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Helper to extract a readable caption from the URL, removing number prefix
  const getCaption = (url: string) => {
    const match = url.match(/gallery\/(\d+)_(.+)$/);
    if (match && match[2]) {
      // Remove extension and replace underscores with spaces
      return match[2].replace(/\.[^/.]+$/, '').replace(/[_+]/g, ' ');
    }
    
    // Fallback for files without number prefix
    const fallbackMatch = url.match(/gallery\/(.+)$/);
    if (fallbackMatch && fallbackMatch[1]) {
      return fallbackMatch[1].replace(/\.[^/.]+$/, '').replace(/[_+]/g, ' ');
    }
    
    return 'Gallery photo';
  };

  if (loading) return <div className="gallery"><div className="container"><p>Loading gallery...</p></div></div>;
  if (error) return <div className="gallery"><div className="container"><p>Error: {error}</p></div></div>;

  return (
    <section id="gallery" className="gallery">
      <div className="container">
        <h2 className="section-title">Our Gallery</h2>
        <p className="gallery-subtitle">Capturing our beautiful moments together</p>
        <div className="gallery-grid">
          {photos.map((url, idx) => (
            <div key={url} className="gallery-item" onClick={() => setSelectedPhoto(url)} style={{ cursor: 'pointer' }}>
              <img 
                src={url} 
                alt={getCaption(url)}
                className="gallery-image"
                loading="lazy"
              />
              <div className="gallery-overlay">
                <span className="gallery-caption">{getCaption(url)}</span>
              </div>
            </div>
          ))}
        </div>
        {selectedPhoto && (
          <div 
            className="gallery-modal-overlay" 
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedPhoto(null);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
              <button 
                onClick={() => setSelectedPhoto(null)}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  fontSize: 20,
                  cursor: 'pointer',
                  zIndex: 1001,
                }}
                aria-label="Close"
              >
                ×
              </button>
              <img 
                src={selectedPhoto} 
                alt={getCaption(selectedPhoto)}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '80vh',
                  borderRadius: 8,
                  boxShadow: '0 4px 32px rgba(0,0,0,0.5)'
                }}
              />
              <div style={{
                color: 'white',
                textAlign: 'center',
                marginTop: 12,
                fontSize: 18,
                textShadow: '0 2px 8px rgba(0,0,0,0.7)'
              }}>{getCaption(selectedPhoto)}</div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery; 